import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  fmtCount,
  fmtMoneyCompact,
  fmtShortDate,
  unclaimedEmergency,
  type CaptureSummary,
  type FleetFacility,
  type GuardianIncident,
  type MetricsOverview,
  type OperatorFleet,
  type PulseFacilities,
} from '@aithora/shared';
import type { QueryState } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { EmptyState, QueryBlock, Skeleton } from '../../ui/states';
import { DevNote } from '../../ui/buildLabels';
import { shortName } from './common';
import { MoneyNote, VolumeBars, notBilledOf, upperMedian, worstFirst, type MoneyRow, type Tile } from './operatorBlocks';

/**
 * The V9 Overview tab for Super Admin, Director and Support (design ovSecIsOverview,
 * L5630-5705, and the ovBalLayer / ovFacilities logic at L39846 and L47709): a today line,
 * then four blocks in a 2 by 2 grid (Facilities, Needs action, Money on the table, Governed
 * volume), Compliance, and the system details folded behind one button.
 *
 * One facility count everywhere: the fleet route's list. The lede, the Facilities heading,
 * "See all N facilities" and the "live" count all read the same list, so they always match.
 */

const HEAD = 'display:flex;align-items:baseline;gap:8px 10px;flex-wrap:wrap;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.14);min-width:0;';
const H_TITLE = 'flex:0 0 auto;font-size:16px;font-weight:700;color:#22272E;';
const H_META = 'flex:1 1 200px;min-width:0;font-size:14px;color:#54606C;line-height:1.35;';
const MORE = 'display:inline-flex;align-items:center;min-height:36px;margin-top:4px;font-size:16px;font-weight:600;color:#0F6B63;text-decoration:none;';

function BlockHead({ title, meta, right }: { title: string; meta?: ReactNode; right?: ReactNode }) {
  return (
    <div style={sx(HEAD)}>
      <span style={sx(H_TITLE)}>{title}</span>
      {meta !== undefined && <span style={sx(H_META)}>{meta}</span>}
      {right}
    </div>
  );
}

// ── Needs action rows ───────────────────────────────────────────────────────

/** Plain words and the one action per tile (design PLAIN and ACT, L39834). */
const NEED: Record<string, [string, string]> = {
  queue: ['Caregiver notes waiting for review', 'Review notes'],
  crisis: ['Crisis escalations', 'Review'],
  drift: ['AI checks drifting from their usual', 'Review flags'],
  median: ['Facilities below the peer median', 'See which'],
  unacked: ['Incidents nobody has acknowledged', 'Review incidents'],
  muted: ['Alert rules switched off', 'Fix alert rules'],
  noesc: ['Escalation paths never tested', 'Run a test'],
  unwired: ['Facilities with no live signal', 'See which'],
  degraded: ['Facilities running degraded', 'See which'],
  anomaly: ['Facilities unusual against their own normal', 'See which'],
  windows: ['Filing windows closing', 'See deadlines'],
  slos: ['Service targets missed', 'See targets'],
};

export interface NeedRow {
  key: string;
  v: string;
  n: number;
  label: string;
  note: string;
  act: string;
  to: string;
  urgent: boolean;
  color: string;
}

/** The tiles as V9 rows: urgent first, then anything open, then the clear ones. */
export function needRows(tiles: Tile[]): NeedRow[] {
  const rank = (t: Tile) => (t.n > 0 ? (t.sev === 'breach' ? 0 : t.sev === 'risk' ? 1 : 2) : 3);
  return tiles
    .map((t, i) => ({ t, i }))
    .sort((a, b) => rank(a.t) - rank(b.t) || a.i - b.i)
    .map(({ t }) => {
      const [label, act] = NEED[t.key] ?? [t.short, 'Open'];
      const urgent = t.sev === 'breach' && t.n > 0;
      return {
        key: t.key, v: t.text ?? fmtCount(t.n), n: t.n, label, note: t.long, act, to: t.to, urgent,
        color: t.n <= 0 ? '#1F6B52' : urgent ? '#B3261E' : t.sev === 'risk' ? '#8A5A00' : '#22272E',
      };
    });
}

/** "N things need you today. Start with: …" (design ovTodayLine, L39856). */
export function todayLine(rows: NeedRow[], emergencyWho: string | null): string {
  const red = rows.filter((r) => r.urgent);
  if (emergencyWho) return `${red.length + 1} things need you today. Start with: ${emergencyWho}’s emergency, nobody has claimed it.`;
  if (red.length) return `${red.length} ${red.length === 1 ? 'thing needs' : 'things need'} you today. Start with: ${red[0].label}.`;
  const open = rows.filter((r) => r.n > 0).length;
  return open ? `Nothing urgent today. ${open} ${open === 1 ? 'item is' : 'items are'} open below.` : 'Nothing urgent today. Nothing is open.';
}

function NeedsList({ rows, loading, notCounted }: { rows: NeedRow[]; loading: boolean; notCounted?: string[] }) {
  const [all, setAll] = useState(false);
  const red = rows.filter((r) => r.urgent).length;
  const open = rows.filter((r) => r.n > 0).length;
  const shown = all ? rows : rows.slice(0, 5);
  return (
    <section aria-label="Needs action" style={sx('min-width:0;')} data-testid="v9-needs">
      <BlockHead title="Needs action" meta={rows.length ? `${red} urgent · ${open} open` : undefined} />
      {loading && !rows.length ? (
        <Skeleton rows={3} />
      ) : !rows.length ? (
        <EmptyState compact title="Nothing counted yet" body="The sources behind this list have not answered. Open Oversight to check them." />
      ) : (
        shown.map((n) => (
          <div key={n.key} data-testid={`need-${n.key}`} style={sx('display:flex;align-items:center;gap:12px;padding:8px 2px;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
            <span style={sx(`flex:0 0 40px;text-align:right;font-size:20px;font-weight:700;font-variant-numeric:tabular-nums;line-height:1;color:${n.color};`)}>{n.v}</span>
            <span style={sx('flex:1 1 auto;min-width:0;')}>
              <span style={sx('display:block;font-size:16px;font-weight:600;color:#22272E;line-height:1.3;')}>{n.label}</span>
              <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.35;')}>{n.note}</span>
            </span>
            <Link to={n.to} style={sx('flex:0 0 auto;display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.35);')}>
              {n.act}
            </Link>
          </div>
        ))
      )}
      {rows.length > 5 && (
        <button type="button" onClick={() => setAll((a) => !a)} style={sx(MORE + 'border:0;background:transparent;padding:0;cursor:pointer;font-family:inherit;')}>
          {all ? 'Show fewer' : `See all ${rows.length}`}
        </button>
      )}
      {notCounted && notCounted.length > 0 && (
        <DevNote>
          <p style={sx('margin:6px 0 0;font-size:14px;color:#54606C;')}>Not counted yet (no route): {notCounted.join(' · ')}</p>
        </DevNote>
      )}
    </section>
  );
}

// ── Facilities cards ────────────────────────────────────────────────────────

/** Worst first, with the 14-day change and where it sits against the peer median (design ovFacilities). */
export function facilityCards(fleet: FleetFacility[], shown = 6) {
  const vals = fleet.map((r) => r.score.value).filter((v): v is number => v !== null);
  const med = upperMedian(vals);
  return worstFirst(fleet)
    .slice(0, shown)
    .map((f) => {
      const c = f.score.value === null ? null : Math.round(f.score.value);
      const t = f.trend?.value ?? null;
      const d = t === null ? null : Math.round(t);
      const vsMed = c === null ? null : c === med ? 'at the peer median' : c > med ? `${c - med} above the peer median` : `${med - c} below the peer median`;
      const move = d === null ? null : d < 0 ? `Down ${-d} over 14 days` : d > 0 ? `Up ${d} over 14 days` : 'Steady over 14 days';
      const tone = c === null ? '#54606C' : c >= 80 ? '#1F6B52' : c >= med ? '#8A5A00' : '#B3261E';
      return {
        id: f.orgId,
        name: f.name,
        score: c === null ? (f.grade ?? '–') : String(c),
        delta: d === null ? null : d === 0 ? 'flat' : `${d > 0 ? '▲' : '▼'} ${Math.abs(d)}`,
        deltaColor: d === null || d === 0 ? '#54606C' : d > 0 ? '#1F6B52' : '#B3261E',
        why: [move, vsMed].filter(Boolean).join(' · ') || 'No composite yet',
        edge: tone,
      };
    });
}

function FacilitiesCards({ q, shown = 6 }: { q: QueryState<OperatorFleet>; shown?: number }) {
  const n = q.data?.facilities.length ?? 0;
  return (
    <section aria-label="Facilities" style={sx('min-width:0;')} data-testid="v9-facilities">
      <BlockHead title="Facilities" meta="Quality score out of 100 · worst first · 14-day trend" />
      <QueryBlock q={q} rows={3} isEmpty={(x) => x.facilities.length === 0} empty={<EmptyState compact title="No facilities yet" body="Facilities appear here once they are onboarded in Settings, Users and Permissions." />}>
        {(x) => (
          <>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,200px),1fr));gap:8px;padding-top:8px;min-width:0;')}>
              {facilityCards(x.facilities, shown).map((f) => (
                <Link
                  key={f.id}
                  to="/performance"
                  title={f.name}
                  style={sx(`display:flex;flex-direction:column;gap:5px;padding:8px 10px 8px 12px;border-radius:7px;border:1px solid rgba(34,39,46,.10);min-width:0;text-decoration:none;color:#22272E;box-shadow:inset 3px 0 0 ${f.edge};`)}
                >
                  <span style={sx('font-size:16px;font-weight:600;line-height:1.25;overflow-wrap:break-word;')}>{f.name}</span>
                  <span style={sx('display:flex;align-items:flex-end;gap:10px;min-width:0;')}>
                    <span style={sx('flex:0 0 auto;display:flex;align-items:baseline;gap:4px;')}>
                      <span style={sx('font-size:20px;font-weight:700;font-variant-numeric:tabular-nums;line-height:1;')}>{f.score}</span>
                      <span style={sx('font-size:14px;color:#54606C;')}>/100</span>
                    </span>
                    <span style={sx('flex:1 1 auto;')} />
                    {f.delta && <span style={sx(`flex:0 0 auto;font-size:14px;font-weight:700;color:${f.deltaColor};`)}>{f.delta}</span>}
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;line-height:1.35;')}>{f.why}</span>
                </Link>
              ))}
            </div>
            <Link to="/performance" style={sx(MORE)} data-testid="v9-facilities-more">
              {n === 1 ? 'See this facility →' : `See all ${fmtCount(n)} facilities →`}
            </Link>
          </>
        )}
      </QueryBlock>
    </section>
  );
}

// ── Money on the table ──────────────────────────────────────────────────────

const flagTint = (days: number | undefined) => (days === undefined ? '#54606C' : days <= 10 ? '#B3261E' : days <= 16 ? '#8A5A00' : '#54606C');

function MoneyGrid({ q, network }: { q: QueryState<MoneyRow[]>; network: QueryState<CaptureSummary> }) {
  return (
    <section aria-label="Money on the table" style={sx('min-width:0;')} data-testid="v9-money">
      <BlockHead title="Money on the table" />
      <QueryBlock q={q} rows={3} isEmpty={(d) => d.length === 0} empty={<EmptyState compact title="No capture figures yet" body="Not-billed dollars appear once a facility’s care is documented. Open Capture to start." />}>
        {(rows) => (
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,200px),1fr));gap:0 16px;padding-top:4px;min-width:0;')}>
            {rows.map(({ facility, summary }) => {
              const days = summary.filingWindow?.closesInDays;
              const f = notBilledOf(summary);
              return (
                <Link
                  key={facility.orgId}
                  to="/capture"
                  title={`${shortName(facility.name)} · not billed this month${days !== undefined ? ` · closes ${days}d` : ''} · open in Capture`}
                  style={sx(`display:flex;align-items:center;gap:8px;padding:6px 6px 6px 9px;margin-bottom:2px;border-radius:5px;min-width:0;text-decoration:none;color:#22272E;box-shadow:inset 3px 0 0 ${flagTint(days)};`)}
                >
                  <span style={sx('flex:1 1 0;min-width:0;font-size:16px;font-weight:600;overflow-wrap:break-word;')}>{shortName(facility.name)}</span>
                  <span data-testid="money-amt" style={sx('flex:0 0 auto;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;')}>{fmtMoneyCompact(f.value === null ? null : f.value / 100)}</span>
                  <HonestyBadge flags={[{ real: summary.real }, { basis: f.basis }]} figure="Money: not billed" className="ov-badge-mini" hide={['MEASURED', 'MODELED', 'ESTIMATED']} />
                  <span aria-hidden="true" style={sx(`flex:0 0 auto;font-size:14px;font-weight:600;color:${flagTint(days)};`)}>
                    {'⚑'}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </QueryBlock>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.4;padding-top:4px;min-width:0;')}>
        <MoneyNote network={network} />
      </div>
    </section>
  );
}

// ── Governed volume ─────────────────────────────────────────────────────────

function GovernedVolume({ q }: { q: QueryState<MetricsOverview> }) {
  const days = q.data?.conversations.recentDays ?? [];
  const counts = days.map((d) => d.count);
  const total = counts.reduce((a, b) => a + b, 0);
  const med = upperMedian(counts);
  return (
    <section aria-label="Governed volume" style={sx('min-width:0;')} data-testid="v9-volume">
      <BlockHead
        title="Governed volume"
        meta={
          <>
            {`last 14 days · ${fmtCount(total)} Atlas conversations`}
            {q.data && <HonestyBadge flags={[{ real: q.data.conversations.real }]} figure="Governed volume" className="ov-badge-mini" hide={['MEASURED']} />}
          </>
        }
        right={
          <Link to="/guardian" style={sx('flex:0 0 auto;font-size:16px;font-weight:600;color:#0F6B63;text-decoration:none;')}>
            Guardian {'→'}
          </Link>
        }
      />
      <QueryBlock q={q} rows={2} isEmpty={(d) => d.conversations.recentDays.length === 0} empty={<EmptyState compact title="No conversations in the window" body="Atlas conversations appear here once residents talk to Atlas." />}>
        {() => (
          <>
            <Link to="/guardian" title="Open Guardian" style={sx('display:block;height:64px;margin-top:10px;text-decoration:none;')}>
              <VolumeBars days={days} H={64} />
            </Link>
            <span style={sx('display:flex;align-items:baseline;justify-content:space-between;gap:8px;flex-wrap:wrap;font-size:14px;color:#54606C;margin-top:4px;')}>
              <span>{fmtShortDate(days[0]?.date)}</span>
              <span>{`typical day ${fmtCount(med)}`}</span>
              <span>{fmtShortDate(days[days.length - 1]?.date)}</span>
            </span>
          </>
        )}
      </QueryBlock>
    </section>
  );
}

// ── The tab ─────────────────────────────────────────────────────────────────

/** Fleet buildings whose every safety link reports ok, counted from the same fleet list. */
export function liveCount(fleet: OperatorFleet | null, facilities: PulseFacilities | null): number | null {
  if (!fleet || !facilities) return null;
  const ok = new Set(facilities.facilities.filter((f) => f.signals.length > 0 && f.signals.every((s) => s.status === 'ok')).map((f) => f.organizationId));
  return fleet.facilities.filter((f) => ok.has(f.orgId)).length;
}

export function OperatorV9Tab({
  lede,
  fleet,
  tiles,
  tilesLoading,
  money,
  network,
  overview,
  incidents,
  compliance,
  details,
  onTriage,
  notCounted,
}: {
  notCounted?: string[];
  lede: ReactNode;
  fleet: QueryState<OperatorFleet>;
  tiles: Tile[];
  tilesLoading: boolean;
  money: QueryState<MoneyRow[]>;
  network: QueryState<CaptureSummary>;
  overview: QueryState<MetricsOverview>;
  incidents: GuardianIncident[] | null;
  compliance: ReactNode;
  details: ReactNode;
  onTriage: () => void;
}) {
  const [sys, setSys] = useState(false);
  const rows = needRows(tiles);
  const em = incidents ? unclaimedEmergency(incidents) : null;
  return (
    <div style={sx('display:flex;flex-direction:column;gap:14px;min-width:0;')} data-testid="overview-operator-v9">
      {lede}
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding:12px 14px;border-radius:10px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;min-width:0;')}>
        <span data-testid="v9-today-line" style={sx('flex:1 1 320px;min-width:0;font-size:17px;font-weight:700;color:#22272E;line-height:1.4;')}>
          {tilesLoading && !rows.length ? ' ' : todayLine(rows, em ? em.residentName ?? 'A resident' : null)}
        </span>
        <button type="button" onClick={onTriage} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;border:0;font-family:inherit;font-size:16px;font-weight:700;white-space:nowrap;color:#FCFAF4;background:#2F5C97;cursor:pointer;')}>
          Open Triage
        </button>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,440px),1fr));gap:22px 32px;flex:0 0 auto;min-width:0;align-items:start;')}>
        <FacilitiesCards q={fleet} />
        <NeedsList rows={rows} loading={tilesLoading} notCounted={notCounted} />
        <MoneyGrid q={money} network={network} />
        <GovernedVolume q={overview} />
      </div>
      {compliance}
      <div style={sx('flex:0 0 auto;')}>
        <button
          type="button"
          aria-expanded={sys}
          data-testid="v9-system-toggle"
          onClick={() => setSys((s) => !s)}
          style={sx('display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:8px;font-family:inherit;font-size:14px;font-weight:700;color:#264C7E;background:transparent;border:1px solid rgba(47,92,151,.35);cursor:pointer;')}
        >
          {sys ? 'Hide system details' : 'Show system details: incidents, alert rules, objectives, facility health'}
        </button>
      </div>
      {sys && details}
    </div>
  );
}
